/**
 * P3 — global Mission Progress Header stage model. Deliberately a COARSE
 * (6-stage) grouping on top of the existing, already-trusted
 * `mapEiStateToUiStage` (11-stage) mapper from `eiLifecycle.ts` — this file
 * invents no new FSM knowledge, it only regroups what that mapper already
 * derives from the frozen EI InvestigationState, for a header simple enough
 * to show on every Operate tab without becoming its own dashboard.
 *
 * Operational missions use a parallel builder keyed off AMS recovery proof
 * (`buildOperationalMissionHeaderStages`) — they must not inherit the EI
 * package-approval rail while recovery already succeeded.
 */
import { mapEiStateToUiStage } from './eiLifecycle';
import type { EiInvestigationRun, EiInvestigationState, EiUiStage } from '../types/engineeringIntelligence';
import type { OperationalRecoveryStatus } from './operationalRemediation';
import {
  engineeringGateMessage,
  engineeringGateParked,
  operatorGuidanceAwaitingResume,
  needsOpenOperatorInput,
} from './operatorGuidanceState';

export type MissionHeaderStageId = 'entry' | 'investigation' | 'engineering' | 'execution' | 'testing' | 'learning';

export type MissionHeaderStageStatus = 'completed' | 'active' | 'blocked' | 'pending';

export interface MissionHeaderStage {
  id: MissionHeaderStageId;
  label: string;
  status: MissionHeaderStageStatus;
}

/**
 * Engineering → Testing → Execution: an approved code package is tested on
 * the run's own working copy and only an explicit Execute promotes it
 * (backend testing_lifecycle.go).
 */
const STAGE_ORDER: MissionHeaderStageId[] = ['entry', 'investigation', 'engineering', 'testing', 'execution', 'learning'];

/** entry's label depends on mission kind ("Incident" for AMS-detected missions, "Request" for Engineering-Request-driven ones) — everything else is shared vocabulary. */
function labelsFor(missionKind: 'incident' | 'request'): Record<MissionHeaderStageId, string> {
  return {
    entry: missionKind === 'request' ? 'Request' : 'Incident',
    investigation: 'Investigation',
    engineering: 'Engineering',
    execution: 'Apply',
    testing: 'Testing',
    learning: 'Learning',
  };
}

/** Delivery-story labels (Himitsu Operate timeline): Registered → … → Executed. */
function himitsuDeliveryLabels(missionKind: 'incident' | 'request'): Record<MissionHeaderStageId, string> {
  return {
    entry: missionKind === 'request' ? 'Registered' : 'Registered',
    investigation: 'Investigated',
    engineering: 'Approved',
    testing: 'Tested',
    execution: 'Executed',
    learning: 'Closed',
  };
}

/**
 * Himitsu timeline step → header stage. Planning and approval are both
 * Engineering; applying the patch to the working copy and running the suite
 * are both Testing. Execution (promotion) and learning have no timeline step
 * and follow the run's state.
 */
const HIMITSU_TO_COARSE: Record<string, MissionHeaderStageId> = {
  registered: 'entry',
  investigated: 'investigation',
  planned: 'engineering',
  approved: 'engineering',
  executed: 'testing',
  tested: 'testing',
};

/** Several timeline steps of one stage: done only when all are, held when any is. */
function mergeStageStatus(a: MissionHeaderStageStatus, b: MissionHeaderStageStatus): MissionHeaderStageStatus {
  if (a === 'blocked' || b === 'blocked') return 'blocked';
  if (a === b) return a;
  return 'active';
}

/** Ops missions share the rail ids (for navigation) but use remediation vocabulary. */
/** Ops missions recover first and validate the recovery after. */
const OPERATIONAL_STAGE_ORDER: MissionHeaderStageId[] = ['entry', 'investigation', 'engineering', 'execution', 'testing', 'learning'];

function operationalLabels(): Record<MissionHeaderStageId, string> {
  return {
    entry: 'Incident',
    investigation: 'Investigation',
    engineering: 'Remediation',
    execution: 'Recovery',
    testing: 'Validation',
    learning: 'Closed',
  };
}

/**
 * The header stage a run state belongs to. Before Execute, applying the
 * patch to the working copy and validating it are Testing; after Execute
 * (a package without code runs its plan then), they are Execution.
 */
function coarseStageForState(s: string, executeRequested: boolean): MissionHeaderStageId | null {
  switch (s) {
    case 'AWAITING_TESTING':
    case 'TESTS_READY':
      return 'testing';
    case 'READY_TO_EXECUTE':
    case 'CONVERGING':
      return 'execution';
    case 'EXECUTING':
    case 'ROLLING_BACK':
    case 'EXECUTION_BLOCKED':
    case 'VALIDATING':
    case 'AGENT_REFLECTION':
    case 'EVALUATE_SUCCESS':
      return executeRequested ? 'execution' : 'testing';
    default:
      return null;
  }
}

function coarseStageFor(uiStage: EiUiStage): MissionHeaderStageId {
  switch (uiStage) {
    case 'incident':
      return 'entry';
    case 'investigation':
    case 'investigation_complete':
      return 'investigation';
    case 'engineering_design':
    case 'package_ready':
    case 'awaiting_approval':
      return 'engineering';
    case 'executing':
      return 'execution';
    case 'validation':
      return 'testing';
    case 'reflection':
    case 'knowledge_update':
    case 'resolved':
      return 'learning';
    case 'needs_human':
    default:
      return 'investigation';
  }
}

/**
 * Builds the 6-stage header model for one mission. NEEDS_HUMAN is treated
 * the same way `buildEiLifecycleStages` (InvestigationLifecycleRail.tsx)
 * already treats it — as a block surfaced at the Testing/Validation stage,
 * the most common real point a human escalation happens — rather than
 * inventing a way to recover which stage actually triggered it (the EI run
 * doesn't expose that history to the frontend today).
 */
export function buildMissionHeaderStages(
  state: EiInvestigationState | string | null | undefined,
  missionKind: 'incident' | 'request' = 'incident',
  /** Execute was requested for the run's package (run.promotion_requested_at). */
  executeRequested = false,
): MissionHeaderStage[] {
  const labels = labelsFor(missionKind);
  const isBlocked = String(state || '').toUpperCase() === 'NEEDS_HUMAN';
  const effective = String((isBlocked ? 'VALIDATING' : state) || '').toUpperCase();
  const coarse =
    coarseStageForState(effective, executeRequested) ??
    coarseStageFor(mapEiStateToUiStage(effective as EiInvestigationState));
  const activeIdx = STAGE_ORDER.indexOf(coarse);

  return STAGE_ORDER.map((id, i) => ({
    id,
    label: labels[id],
    status: i < activeIdx ? 'completed' : i === activeIdx ? (isBlocked ? 'blocked' : 'active') : 'pending',
  }));
}

/** Run states in which the mission is finished — nothing further will happen. */
export const EI_DONE_STATES = new Set([
  'RESOLVED', 'CLOSED', 'INVALIDATED', 'INVESTIGATION_COMPLETE', 'DIAGNOSED',
  'LEARNING', 'REFLECTION', 'KNOWLEDGE_UPDATE',
]);

/** Run states in which the mission is stopped until a person acts. */
export const EI_NEEDS_YOU_STATES = new Set([
  'NEEDS_HUMAN', 'AWAITING_APPROVAL', 'IMPLEMENTATION_PACKAGE_READY',
  'AWAITING_TESTING', 'TESTS_READY', 'READY_TO_EXECUTE',
]);

/** States where the run cannot move on by itself: the current stage is held up. */
const EI_HELD_STATES = new Set(['NEEDS_HUMAN', 'EXECUTION_BLOCKED']);

/**
 * A stage timeline must never claim more than the run's state supports.
 *
 * The backend used to mark "Tested" completed as soon as validation ran —
 * even when the required mission tests failed and the run went to
 * NEEDS_HUMAN — so the rail read 100% "done" beside a "Needs you" cue.
 * Runs persisted before that fix still carry the wrong timeline, so the rule
 * is enforced here: while the run is not finished, the last stage cannot be
 * completed. It is held up (blocked) when the run waits on a person or a
 * blocked capability, otherwise still in progress.
 */
export function reconcileTimelineWithState<T extends { id?: string; status?: string; detail?: string }>(
  steps: T[],
  state: string | null | undefined,
  heldDetail?: string,
): T[] {
  const s = String(state || '').toUpperCase();
  if (!steps.length || !s) return steps;
  steps = holdActiveStage(completeStagesTheStateProves(steps, s), s, heldDetail);
  if (EI_DONE_STATES.has(s)) return steps;
  if (!steps.every((step) => String(step.status || '').toLowerCase() === 'completed')) return steps;
  const held = EI_HELD_STATES.has(s);
  const last = steps[steps.length - 1];
  return [
    ...steps.slice(0, -1),
    {
      ...last,
      status: held ? 'blocked' : 'active',
      detail: held && heldDetail ? heldDetail : last.detail,
    },
  ];
}

/** Run states only reachable once the investigation concluded. CLOSED is not one: a run can be closed mid-investigation. */
const EI_INVESTIGATION_CONCLUDED_STATES = new Set([
  'INVESTIGATION_COMPLETE', 'DIAGNOSED', 'PLANNING', 'ENGINEERING_DESIGN', 'IMPLEMENTATION_PACKAGE_READY', 'AWAITING_APPROVAL',
  'AWAITING_TESTING', 'TESTS_READY', 'READY_TO_EXECUTE',
  'EXECUTING', 'VALIDATING', 'AGENT_REFLECTION', 'EVALUATE_SUCCESS', 'RESOLVED', 'CONVERGING', 'LEARNING', 'REFLECTION', 'KNOWLEDGE_UPDATE',
]);

/** Run states only reachable after the package was approved (CLOSED excluded, as above). */
const EI_APPROVED_STATES = new Set([
  'AWAITING_TESTING', 'TESTS_READY', 'READY_TO_EXECUTE',
  'EXECUTING', 'VALIDATING', 'AGENT_REFLECTION', 'EVALUATE_SUCCESS', 'RESOLVED', 'CONVERGING', 'LEARNING', 'REFLECTION', 'KNOWLEDGE_UPDATE',
]);

/**
 * Timeline stages the run's state proves finished are shown completed.
 * Runs used to settle at INVESTIGATION_COMPLETE with "Investigated" still
 * active, and the rail read the investigation as in progress. Only earlier
 * stages are promoted; the next stage is left as recorded, since finishing
 * one stage does not mean the run has entered the next.
 */
function completeStagesTheStateProves<T extends { id?: string; status?: string }>(steps: T[], s: string): T[] {
  const done = new Set<string>();
  if (EI_INVESTIGATION_CONCLUDED_STATES.has(s)) ['registered', 'investigated'].forEach((id) => done.add(id));
  if (EI_APPROVED_STATES.has(s)) ['planned', 'approved'].forEach((id) => done.add(id));
  // The run is designing: a planning stage still recorded as pending is under way.
  const designing = s === 'PLANNING' || s === 'ENGINEERING_DESIGN';
  if (!done.size) return steps;
  return steps.map((step) => {
    const id = String(step.id || '');
    const status = String(step.status || '').toLowerCase();
    if (done.has(id) && status !== 'completed') return { ...step, status: 'completed' };
    if (designing && id === 'planned' && status === 'pending') return { ...step, status: 'active' };
    return step;
  });
}

/**
 * A run waiting on a person is not progressing: its active stage is held up.
 * With none active (a run parked at the engineering gate after its
 * investigation completed), the stage it cannot enter is the held one.
 */
function holdActiveStage<T extends { id?: string; status?: string; detail?: string }>(steps: T[], s: string, heldDetail?: string): T[] {
  if (!EI_HELD_STATES.has(s)) return steps;
  const statusOf = (step: T) => String(step.status || '').toLowerCase();
  let held = steps.findIndex((step) => statusOf(step) === 'active' || statusOf(step) === 'blocked');
  if (held === -1) {
    // Only design can be waited on this way; later stages are held by capStagesAtApproval.
    const next = steps.findIndex((step) => statusOf(step) === 'pending');
    held = next !== -1 && steps[next].id === 'planned' ? next : -1;
  }
  if (held === -1 || statusOf(steps[held]) === 'blocked') return steps;
  return steps.map((step, i) => (i === held ? { ...step, status: 'blocked', detail: heldDetail || step.detail } : step));
}

/**
 * Prefer the Himitsu Adaptive delivery timeline when the run carries one —
 * Registered → Investigated → Planned → Approved → Executed → Tested.
 * Falls back to buildMissionHeaderStages when himitsu.timeline is absent.
 */
export function buildMissionHeaderStagesFromHimitsu(
  himitsu: { timeline?: Array<{ id?: string; label?: string; status?: string }> } | null | undefined,
  state: EiInvestigationState | string | null | undefined,
  missionKind: 'incident' | 'request' = 'incident',
  /** The implementation package was approved (run.approved_at). */
  approved = false,
  /** Execute was requested for the run's package (run.promotion_requested_at). */
  executeRequested = false,
): MissionHeaderStage[] {
  const raw = himitsu?.timeline;
  if (!raw || raw.length === 0) {
    return buildMissionHeaderStages(state, missionKind, executeRequested);
  }
  const steps = reconcileTimelineWithState(raw, state);
  const labels = himitsuDeliveryLabels(missionKind);
  const byCoarse = new Map<MissionHeaderStageId, MissionHeaderStageStatus>();
  for (const step of steps) {
    const coarse = HIMITSU_TO_COARSE[String(step.id || '')];
    if (!coarse) continue;
    const raw = String(step.status || 'pending').toLowerCase();
    const status: MissionHeaderStageStatus =
      raw === 'completed' || raw === 'active' || raw === 'blocked' || raw === 'pending'
        ? (raw as MissionHeaderStageStatus)
        : 'pending';
    const prev = byCoarse.get(coarse);
    byCoarse.set(coarse, prev ? mergeStageStatus(prev, status) : status);
  }
  // Stages the timeline does not record (Execution, learning) follow the
  // run's state. So does the stage an approved run's state places it in when
  // the timeline still has it pending (AWAITING_TESTING before any step ran).
  const s = String(state || '').toUpperCase();
  const fallback = buildMissionHeaderStages(state, missionKind, executeRequested);
  const stateStage = EI_POST_APPROVAL_STATES.has(s) ? fallback.find((st) => st.status === 'active')?.id : undefined;
  const timelineShowsTesting = Boolean(byCoarse.get('testing') && byCoarse.get('testing') !== 'pending');
  let stages: MissionHeaderStage[] = STAGE_ORDER.map((id, i) => {
    const hit = byCoarse.get(id);
    if (!hit || (hit === 'pending' && id === stateStage)) {
      return { id, label: labels[id], status: fallback[i]?.status || 'pending' };
    }
    return { id, label: labels[id], status: hit };
  });
  // Once the run's state has moved on to a later stage (e.g. Testing accepted,
  // now in Execution), earlier stages are done even if the timeline still
  // carries an old blocked/active step for them.
  const liveIdx = stateStage ? STAGE_ORDER.indexOf(stateStage) : -1;
  if (liveIdx > 0 && (stateStage === 'testing' || stateStage === 'execution')) {
    stages = stages.map((st, i) => (i < liveIdx && st.status !== 'completed' ? { ...st, status: 'completed' } : st));
  }
  // After approval, a later stage that is under way or done means Testing
  // passed: a stale blocked/active Testing step is done.
  if (EI_POST_APPROVAL_STATES.has(s)) {
    const testIdx = STAGE_ORDER.indexOf('testing');
    const laterReached = stages.some(
      (st, i) => i > testIdx && st.id !== 'learning' && (st.status === 'completed' || st.status === 'active'),
    );
    if (laterReached) {
      stages = stages.map((st, i) => (i === testIdx && (st.status === 'blocked' || st.status === 'active') ? { ...st, status: 'completed' } : st));
    }
  }
  // A closed run whose recorded stages are all done is done throughout.
  if (s === 'CLOSED' && [...byCoarse.values()].every((st) => st === 'completed') && byCoarse.get('testing') === 'completed') {
    stages = stages.map((st) => ({ ...st, status: 'completed' }));
  }
  return capStagesAtApproval(stages, state, approved, timelineShowsTesting);
}

/** States only reachable after the implementation package was approved. */
const EI_POST_APPROVAL_STATES = new Set([
  'AWAITING_TESTING', 'TESTS_READY', 'READY_TO_EXECUTE',
  'EXECUTING', 'VALIDATING', 'AGENT_REFLECTION', 'EVALUATE_SUCCESS', 'RESOLVED',
  'CONVERGING', 'LEARNING', 'REFLECTION', 'KNOWLEDGE_UPDATE', 'CLOSED',
]);

/**
 * Nothing past "Approved" can be under way, and "Approved" itself cannot be
 * done, before the package is approved. Older runs carry timelines where a
 * business answer marked "Approved" completed while the design was still
 * being worked on; the rail must follow the run, not that timeline.
 */
export function capStagesAtApproval(
  stages: MissionHeaderStage[],
  state: string | null | undefined,
  approved: boolean,
  /** The run's own timeline records Testing under way or done. */
  timelineShowsTesting = false,
): MissionHeaderStage[] {
  const s = String(state || '').toUpperCase();
  if (!s || approved || EI_POST_APPROVAL_STATES.has(s)) return stages;
  const awaiting = s === 'AWAITING_APPROVAL' || s === 'IMPLEMENTATION_PACKAGE_READY';
  const held = EI_HELD_STATES.has(s);
  const engIdx = STAGE_ORDER.indexOf('engineering');
  // Testing recorded on the timeline is itself proof the package was approved.
  if (timelineShowsTesting) return stages;
  return stages.map((st, i) => {
    if (i > engIdx) return { ...st, status: 'pending' };
    if (i === engIdx) {
      if (awaiting) return { ...st, status: 'active' };
      return st.status === 'pending' ? st : { ...st, status: held ? 'blocked' : 'active' };
    }
    return st;
  });
}

export function missionHeaderNextStage(stages: MissionHeaderStage[]): MissionHeaderStage | null {
  const activeIdx = stages.findIndex((s) => s.status === 'active' || s.status === 'blocked');
  if (activeIdx === -1 || activeIdx === stages.length - 1) return null;
  return stages[activeIdx + 1];
}

/**
 * Stage rail for OPERATIONAL missions — driven by AMS recovery assessment,
 * not EI AWAITING_APPROVAL / package readiness.
 *
 * Mapping:
 *   idle              → Remediation active (apply ops fix)
 *   running           → Recovery active (execute/validate in flight)
 *   failed            → Recovery blocked
 *   awaiting_upstream → Validation blocked (waiting on an upstream system)
 *   needs_human       → Validation blocked (needs transfer logs)
 *   concluded_unresolved → Validation blocked / Closed not recovery-complete
 *   verified          → Validation complete; Closed pending
 *   closed            → all stages completed
 */
export function buildOperationalMissionHeaderStages(
  recoveryStatus: OperationalRecoveryStatus,
): MissionHeaderStage[] {
  const labels = operationalLabels();
  let activeId: MissionHeaderStageId = 'engineering';
  let blocked = false;
  let allComplete = false;

  switch (recoveryStatus) {
    case 'idle':
      activeId = 'engineering';
      break;
    case 'running':
      activeId = 'execution';
      break;
    case 'failed':
      activeId = 'execution';
      blocked = true;
      break;
    case 'awaiting_upstream':
    case 'needs_human':
      activeId = 'testing';
      blocked = true;
      break;
    case 'concluded_unresolved':
      activeId = 'learning';
      blocked = true;
      break;
    case 'verified':
      activeId = 'learning';
      break;
    case 'closed':
      allComplete = true;
      break;
  }

  const activeIdx = OPERATIONAL_STAGE_ORDER.indexOf(activeId);
  return OPERATIONAL_STAGE_ORDER.map((id, i) => {
    if (allComplete) {
      return { id, label: labels[id], status: 'completed' as const };
    }
    // verified: Incident…Validation completed, Closed active (operator may still dismiss)
    if (recoveryStatus === 'verified') {
      if (id === 'learning') return { id, label: labels[id], status: 'active' as const };
      return { id, label: labels[id], status: 'completed' as const };
    }
    return {
      id,
      label: labels[id],
      status: (i < activeIdx
        ? 'completed'
        : i === activeIdx
          ? blocked
            ? 'blocked'
            : 'active'
          : 'pending') as MissionHeaderStageStatus,
    };
  });
}

/** Chip label for ops recovery — never "Awaiting Approval". */
export function operationalRecoveryStateLabel(status: OperationalRecoveryStatus): string {
  switch (status) {
    case 'idle':
      return 'Awaiting remediation';
    case 'running':
      return 'Recovering';
    case 'verified':
      return 'Recovery verified';
    case 'failed':
      return 'Recovery failed';
    case 'needs_human':
      return 'Needs transfer logs';
    case 'awaiting_upstream':
      return 'Awaiting upstream';
    case 'concluded_unresolved':
      return 'Concluded · unresolved';
    case 'closed':
      return 'Closed';
  }
}

/** Operator cue derived from ops recovery (not EI package FSM). */
export function operationalMissionHeaderCue(
  status: OperationalRecoveryStatus,
  message: string,
): MissionHeaderOperatorCue | null {
  switch (status) {
    case 'failed':
    case 'needs_human':
    case 'awaiting_upstream':
    case 'concluded_unresolved':
      return { kind: 'needs_you', message };
    case 'closed':
    case 'verified':
      return { kind: 'status', message };
    case 'running':
    case 'idle':
      return { kind: 'next_action', message };
  }
}

/** Overall progress, 0-100: completed stages fully counted, the active/blocked
 * one counted as half-done — a plain, honest fraction of the 6-stage rail,
 * not a fabricated "AI confidence" number. */
export function missionHeaderProgressPercent(stages: MissionHeaderStage[]): number {
  if (stages.length === 0) return 0;
  const completed = stages.filter((s) => s.status === 'completed').length;
  const inFlight = stages.some((s) => s.status === 'active' || s.status === 'blocked') ? 0.5 : 0;
  return Math.round(((completed + inFlight) / stages.length) * 100);
}

/** Plain-language "what is EnsoMirae++ doing right now" — reuses the
 * existing, already-shipped `mapEiStateToOperatorPhase` classification
 * (Observing/Reasoning/Deciding/Designing/…) rather than inventing new
 * copy for the same concept. */
export { operatorPhaseLabel as missionHeaderCurrentActivity } from './eiLifecycle';

/** What (if anything) the operator should do next — derived only from the
 * run's own state. Hard escalations (`needs_you`) are NEEDS_HUMAN only;
 * package review / approval waits are soft next actions, never labeled
 * "Needs you" (that conflation made Approval look like a blocked escalation). */
export type MissionHeaderOperatorCue =
  | { kind: 'needs_you'; message: string }
  | { kind: 'next_action'; message: string }
  /** Terminal / informational — not an outstanding operator action. */
  | { kind: 'status'; message: string };

export function missionHeaderOperatorCue(
  state: EiInvestigationState | string | null | undefined,
  run?: EiInvestigationRun | null,
): MissionHeaderOperatorCue | null {
  const s = String(state || '').toUpperCase();
  if (s === 'NEEDS_HUMAN') {
    if (run && engineeringGateParked(run)) {
      return { kind: 'needs_you', message: engineeringGateMessage(run) };
    }
    if (run && operatorGuidanceAwaitingResume(run)) {
      return { kind: 'status', message: 'Your response was recorded — automation is continuing' };
    }
    if (run && !needsOpenOperatorInput(run)) {
      return { kind: 'status', message: 'Automation is processing your last input' };
    }
    return { kind: 'needs_you', message: 'Answer the open question so automation can continue' };
  }
  if (s === 'AWAITING_APPROVAL') {
    return { kind: 'next_action', message: 'Review and approve the Implementation Package when ready' };
  }
  if (s === 'IMPLEMENTATION_PACKAGE_READY') {
    return { kind: 'next_action', message: 'Review the sealed Implementation Package' };
  }
  if (s === 'AWAITING_TESTING') {
    return { kind: 'next_action', message: 'Approved — start Testing when ready (nothing has been applied yet)' };
  }
  if (s === 'TESTS_READY') {
    return { kind: 'next_action', message: 'Review the test results and accept them, rerun, or request changes' };
  }
  if (s === 'READY_TO_EXECUTE') {
    return { kind: 'next_action', message: 'Ready to execute — Execute applies the tested change to the project repository' };
  }
  return null;
}

/** @deprecated Prefer {@link missionHeaderOperatorCue} — kept for callers that only need NEEDS_HUMAN text. */
export function missionHeaderHumanActionRequired(state: EiInvestigationState | string | null | undefined): string | null {
  const cue = missionHeaderOperatorCue(state);
  return cue?.kind === 'needs_you' ? cue.message : null;
}

export type MissionHeaderRiskTier = 'low' | 'medium' | 'high' | 'critical';

const RISK_KEYS = ['risk_level', 'overall_risk', 'level', 'overall', 'severity', 'risk_tier'];
const RISK_VALUE_MAP: Record<string, MissionHeaderRiskTier> = {
  low: 'low', minor: 'low',
  medium: 'medium', moderate: 'medium', mid: 'medium',
  high: 'high', major: 'high', elevated: 'high',
  critical: 'critical', severe: 'critical', extreme: 'critical',
};

/**
 * Best-effort extraction of a coarse risk tier from the sealed package's
 * own `risk_assessment` — an intentionally opaque `Record<string, unknown>`
 * at the type level (no P0/P1 contract guarantees its shape). Returns null
 * rather than a fabricated tier when nothing recognizable is present —
 * the header hides the Risk chip entirely in that case instead of
 * guessing.
 */
export function extractMissionRiskTier(riskAssessment: Record<string, unknown> | undefined | null): MissionHeaderRiskTier | null {
  if (!riskAssessment) return null;
  for (const key of RISK_KEYS) {
    const raw = riskAssessment[key];
    if (typeof raw === 'string') {
      const normalized = RISK_VALUE_MAP[raw.trim().toLowerCase()];
      if (normalized) return normalized;
    }
  }
  return null;
}

const TIER_RANK: Record<MissionHeaderRiskTier, number> = { low: 1, medium: 2, high: 3, critical: 4 };

/**
 * The package tier raised to the sealed patch's blast radius grade when that
 * is higher. Packages sealed before the radius was folded into
 * risk_assessment otherwise read "Low risk" in the header for a patch whose
 * reach graded high. An unknown radius raises nothing here — it is not a
 * tier — and the header flags it separately.
 */
export function missionRiskTierWithBlastRadius(
  tier: MissionHeaderRiskTier | null,
  blastRiskLevel: string | undefined,
): MissionHeaderRiskTier | null {
  const blast = RISK_VALUE_MAP[(blastRiskLevel || '').trim().toLowerCase()];
  if (!blast) return tier;
  if (!tier) return blast;
  return TIER_RANK[blast] > TIER_RANK[tier] ? blast : tier;
}
